import { menuPagePath } from '@/domain/catalog/menu-links';
import type {
  IDraftDelta,
  IMenuDraft,
  IMenuImportPage,
  IMenuImportRead,
  TDraftOp,
  TDraftQuestion,
  TMenuImportPageOrigin,
  TMenuImportPageStatus,
  TReviewReason,
} from '@/interfaces/IMenuImport';
import { PAGES_PER_READ } from './capture-files';
import {
  draftHasItems,
  firstRedItemId,
  orderedItems,
  unpricedVisibleItemIds,
} from './menu-draft';
import type { IDraftSummary } from './menu-draft';
import { groupOwnerPages } from './owner-pages';
import type { TPublishSource } from './publish-dialog';
import { viewerPages } from './source-viewer';

/**
 * Pure rules of the menu workspace (plan §4.4, §4.5): which state it shows,
 * the reading progress, the failed pages, the bottom bar and the targets of
 * «Publicar menú» and «Seguir después». The components only render them.
 */

// ---------------------------------------------------------------------------
// Copy keys that depend on a server code
// ---------------------------------------------------------------------------

/** The read and page failure codes that have a `menuImport.reasons.*` text. */
const READ_REASON_CODES: ReadonlySet<string> = new Set([
  'NOT_MENU',
  'UNREADABLE',
  'NOTHING_FOUND',
  'TOO_DENSE',
  'TIMEOUT',
  'MODEL_ERROR',
  'AI_UNAVAILABLE',
  'READ_LIMIT',
  'SESSION_BUDGET',
  'INTERRUPTED',
]);

/** Why a part of a long page found no dishes: a banner or a footer, not a failure when the rest of its page had dishes. */
const EMPTY_PART_CODES: ReadonlySet<string> = new Set([
  'NOT_MENU',
  'NOTHING_FOUND',
]);

/** Page failures a read of the same stored pages can fix (the API retries exactly these). */
const RETRYABLE_PAGE_CODES: ReadonlySet<string> = new Set([
  'MODEL_ERROR',
  'TIMEOUT',
]);

/** Failures where «Intentar de nuevo» reads the same pages again (§4.4). */
const RETRYABLE_READ_REASONS: ReadonlySet<string> = new Set([
  'MODEL_ERROR',
  'TIMEOUT',
  'INTERRUPTED',
]);

/** Review reasons with a `menuImport.check.*` text (the amber ones). */
const CHECK_REASONS: ReadonlySet<TReviewReason> = new Set<TReviewReason>([
  'price_pre_tax',
  'price_unclear',
  'foreign_currency',
  'price_outlier',
  'name_unclear',
  'sizes_unclear',
  'addons_unclear',
  'possible_duplicate',
  'inferred_category',
  'combo',
  'cut_off',
  'handwritten',
  'generated_text',
  'allergens',
  'addons',
]);

/** `menuImport.reasons.{code}`, or null for a code without a text. */
export function readReasonKey(code: string | null): string | null {
  return code !== null && READ_REASON_CODES.has(code)
    ? `menuImport.reasons.${code}`
    : null;
}

/** A read that failed for this reason can be read again from the same pages. */
export function isRetryableReadReason(reason: string | null): boolean {
  return reason !== null && RETRYABLE_READ_REASONS.has(reason);
}

/** «Intentar de nuevo» is offered: the latest read failed on our side. */
export function canRetryRead(read: IMenuImportRead | null): boolean {
  return (
    read !== null &&
    read.status === 'failed' &&
    isRetryableReadReason(read.reason)
  );
}

/** `menuImport.check.{reason}` («Revise: el precio»), or null without a text. */
export function checkReasonKey(reason: TReviewReason | null): string | null {
  return reason !== null && CHECK_REASONS.has(reason)
    ? `menuImport.check.${reason}`
    : null;
}

// ---------------------------------------------------------------------------
// Workspace state
// ---------------------------------------------------------------------------

export type TWorkspacePhase = 'failed' | 'reading' | 'review';

/**
 * Reading while the latest read runs; the failed state while the latest read
 * failed and nothing was built yet (no dish, no category, not building by
 * hand); otherwise the review.
 */
export function workspacePhase(
  read: IMenuImportRead | null,
  draft: IMenuDraft | null,
  manual: boolean
): TWorkspacePhase {
  if (read?.status === 'running') return 'reading';
  if (
    read?.status === 'failed' &&
    !manual &&
    !draftHasItems(draft) &&
    (draft?.categoryOrder.length ?? 0) === 0
  )
    return 'failed';
  return 'review';
}

/** The latest read just ended with dishes: the one-time «Terminamos de leer» card. */
export function isReadFinish(
  previous: IMenuImportRead | null,
  next: IMenuImportRead | null
): boolean {
  return (
    previous !== null &&
    next !== null &&
    previous.readNo === next.readNo &&
    previous.status === 'running' &&
    (next.status === 'done' || next.status === 'partial')
  );
}

// ---------------------------------------------------------------------------
// Reading progress
// ---------------------------------------------------------------------------

/** The pages of a read, in the order they were added. */
export function readPages(
  read: IMenuImportRead,
  pages: IMenuImportPage[]
): IMenuImportPage[] {
  return pages
    .filter((page) => page.readNo === read.readNo)
    .sort((a, b) => a.position - b.position);
}

/** The words of a read that depend on what the owner sent. */
export interface IReadCopyKeys {
  /** The first stage of the progress card. */
  checking: string;
  /** The third stage («Ordenando su menú», or «Comparando con su menú» in an update). */
  organizing: string;
  /** The message of the «¿Dejar de leer?» confirmation. */
  cancelMessage: string;
}

/**
 * A read of typed, pasted or spreadsheet text only (every page of it is a
 * text page) speaks of «su carta» and «lo que escribió», not of photos
 * (review round 5, m4). Photo or mixed reads, and a read whose pages are
 * not known yet, keep the photo words. A read of a store with dishes
 * (`update`) compares them with its menu at the end.
 */
export function readCopyKeys(
  read: IMenuImportRead | null,
  pages: IMenuImportPage[],
  update = false
): IReadCopyKeys {
  const own = read ? readPages(read, pages) : [];
  const textOnly = own.length > 0 && own.every((page) => page.kind === 'text');
  const organizing = update
    ? 'menuImport.update.readingStage'
    : 'menuImport.reading.organizing';
  return textOnly
    ? {
        checking: 'menuImport.reading.checkingText',
        organizing,
        cancelMessage: 'menuImport.reading.cancelMessageText',
      }
    : {
        checking: 'menuImport.reading.checking',
        organizing,
        cancelMessage: 'menuImport.reading.cancelMessage',
      };
}

export type TReadChipState = 'done' | 'now' | 'failed' | 'pending';

export interface IReadChip {
  /** 1-based, in the read's order: the page as the owner added it (the parts of a long page are one). */
  page: number;
  state: TReadChipState;
}

export interface IReadProgressModel {
  total: number;
  /** «Leyendo la página {current} de {total}»: the lowest page still reading. */
  current: number;
  chips: IReadChip[];
  /** Every page is read: the read is sorting categories and prices. */
  organizing: boolean;
}

const CHIP_STATES: Readonly<Record<TMenuImportPageStatus, TReadChipState>> = {
  uploaded: 'pending',
  queued: 'pending',
  reading: 'now',
  done: 'done',
  failed: 'failed',
};

/**
 * The chip of a page the owner added, from its images: pending until one
 * starts, reading while any is still to read, then done when any part was
 * read (a part that is only a banner or a footer fails alone), else failed.
 */
function ownerPageState(images: IMenuImportPage[]): TReadChipState {
  const states = images.map((page) => CHIP_STATES[page.status]);
  if (states.every((state) => state === 'pending')) return 'pending';
  if (states.some((state) => state === 'now' || state === 'pending'))
    return 'now';
  return states.includes('done') ? 'done' : 'failed';
}

function firstPageIn(chips: IReadChip[], state: TReadChipState): number | null {
  return chips.find((chip) => chip.state === state)?.page ?? null;
}

/**
 * The progress card of a running read: one chip per page the owner added
 * from the page rows, or one per image from the read's counters while the
 * page rows are not known yet.
 */
export function readProgressModel(
  read: IMenuImportRead,
  pages: IMenuImportPage[]
): IReadProgressModel {
  const organizing = read.stage === 'organizing';
  const own = readPages(read, pages);
  if (own.length > 0) {
    const chips = groupOwnerPages(own).map(
      (images, index): IReadChip => ({
        page: index + 1,
        state: ownerPageState(images),
      })
    );
    return {
      total: chips.length,
      current:
        firstPageIn(chips, 'now') ??
        firstPageIn(chips, 'pending') ??
        chips.length,
      chips,
      organizing,
    };
  }

  const total = Math.max(read.pagesTotal, 0);
  const done = organizing
    ? total
    : Math.min(Math.max(read.pagesDone, 0), total);
  const chips = Array.from(
    { length: total },
    (_, index): IReadChip => {
      const page = index + 1;
      if (page <= done) return { page, state: 'done' };
      return { page, state: page === done + 1 ? 'now' : 'pending' };
    }
  );
  return {
    total,
    current: total === 0 ? 0 : Math.min(done + 1, total),
    chips,
    organizing,
  };
}

/** «{count} platos encontrados»: the read's counter, or the dishes of the read already in the draft when ahead. */
export function readFoundCount(
  read: IMenuImportRead,
  draft: IMenuDraft | null
): number {
  const inDraft = draft
    ? Object.values(draft.items).filter((item) => item.readNo === read.readNo)
        .length
    : 0;
  return Math.max(read.itemsFound, inDraft);
}

/** After this long without the end, the card says it is taking longer than usual… */
export const READ_SLOW_MS = 90000;
/** …plus this for each image beyond PAGES_PER_READ (the parts of very tall pages). */
export const READ_SLOW_PER_EXTRA_IMAGE_MS = 4500;

/** When a read of `images` pages or parts counts as slow. */
export function readSlowMs(images: number): number {
  return (
    READ_SLOW_MS +
    Math.max(0, images - PAGES_PER_READ) * READ_SLOW_PER_EXTRA_IMAGE_MS
  );
}

/**
 * Milliseconds until a read of `images` pages or parts counts as slow (0 =
 * already slow). A start in the future (clock skew) counts from now.
 */
export function readSlowDelay(
  startedAt: string,
  now: number,
  images: number
): number {
  const slow = readSlowMs(images);
  const started = Date.parse(startedAt);
  if (!Number.isFinite(started)) return slow;
  const elapsed = now - started;
  if (elapsed < 0) return slow;
  return Math.max(0, slow - elapsed);
}

/** A page the owner added that the latest read could not read, whole or in part. */
export interface IRetakePage {
  /** Its first image: a stable key. */
  key: string;
  /** Its images that could not be read or were too dense («Seguir sin ella» hides them all). */
  pageIds: string[];
  /** Its number inside the read («No pudimos leer la página 2»). */
  pageNumber: number;
  /** The most frequent reason among those images. */
  errorCode: string | null;
  /** A very tall page read in parts: «una parte de la página 2». */
  inParts: boolean;
  origin: TMenuImportPageOrigin;
  /** It failed on our side (the model, the time): reading the stored images again can work. */
  retryable: boolean;
  /** «Seguir sin ella»: not for a page read with too many dishes (it has dishes already). */
  canSkip: boolean;
}

/** The most frequent code, the first one on a tie. */
function mostFrequent(codes: (string | null)[]): string | null {
  const counts = new Map<string | null, number>();
  for (const code of codes) counts.set(code, (counts.get(code) ?? 0) + 1);
  let best: string | null = codes.length > 0 ? codes[0] : null;
  for (const [code, count] of counts) {
    if (count > (counts.get(best) ?? 0)) best = code;
  }
  return best;
}

/**
 * The pages of the latest finished read that could not be read, or were too
 * dense (§4.4), one per page the owner added, numbered as they added them. A
 * part of a long page that found no dishes while another part of it did (a
 * banner, a footer) is not one of them.
 */
export function retakePages(
  read: IMenuImportRead | null,
  pages: IMenuImportPage[]
): IRetakePage[] {
  if (!read || read.status === 'running' || read.status === 'cancelled')
    return [];
  return groupOwnerPages(readPages(read, pages)).flatMap(
    (group, index): IRetakePage[] => {
      const found = group.some(
        (page) => page.status === 'done' && page.itemsFound > 0
      );
      const retakes = group.filter((page) => {
        if (page.status === 'failed')
          return !(
            found &&
            page.originPart !== null &&
            page.errorCode !== null &&
            EMPTY_PART_CODES.has(page.errorCode)
          );
        return page.status === 'done' && page.errorCode === 'TOO_DENSE';
      });
      if (retakes.length === 0) return [];
      return [
        {
          key: group[0].pageId,
          pageIds: retakes.map((page) => page.pageId),
          pageNumber: index + 1,
          errorCode: mostFrequent(retakes.map((page) => page.errorCode)),
          inParts: group[0].originPart !== null,
          origin: group[0].origin,
          retryable: retakes.some(
            (page) =>
              page.errorCode !== null &&
              RETRYABLE_PAGE_CODES.has(page.errorCode)
          ),
          canSkip: retakes.every((page) => page.status === 'failed'),
        },
      ];
    }
  );
}

/**
 * The latest read left something out: it ended `partial`, or one of its
 * pages failed or was cut for density (a `TOO_DENSE` page ends `done`, so
 * the read can end `done` too). The «Terminamos de leer» card and the
 * summary both follow this one rule: never «Todo listo para publicar» next
 * to a «No pudimos leer la página N» card (review round 4, m1).
 */
export function isReadIncomplete(
  read: IMenuImportRead | null,
  pages: IMenuImportPage[]
): boolean {
  return (
    read !== null &&
    (read.status === 'partial' || retakePages(read, pages).length > 0)
  );
}

// ---------------------------------------------------------------------------
// Review
// ---------------------------------------------------------------------------

/** Open questions, the blocking ones first, each group in the draft's order. */
export function openQuestions(draft: IMenuDraft): TDraftQuestion[] {
  const open = draft.questionOrder
    .map((id) => draft.questions[id])
    .filter(
      (question): question is TDraftQuestion =>
        question !== undefined && question.status === 'open'
    );
  return [
    ...open.filter((question) => question.blocking),
    ...open.filter((question) => !question.blocking),
  ];
}

/** The summary sentence and its bold dish count. */
export interface ISummaryKeys {
  /** `{dishes}` and `{categories}` slots. */
  sentence: string;
  /** Two-form key for the `{dishes}` slot (`$tc`). */
  dishes: string;
}

/**
 * «Encontramos {dishes} en {categories}» only when the AI read the dishes;
 * a copied menu or one built by hand «tiene» them (`publishSource`; review
 * round 5, m4). The dish count has its own key for the second sentence so
 * each language can inflect it there.
 */
export function summaryKeys(source: TPublishSource): ISummaryKeys {
  return source === 'read'
    ? {
        sentence: 'menuImport.review.summary',
        dishes: 'menuImport.review.summaryDishes',
      }
    : {
        sentence: 'menuImport.review.summaryOwn',
        dishes: 'menuImport.review.summaryOwnDishes',
      };
}

/** The summary sentences of an update session (plan §3.5 «Summary card»). */
export interface IUpdateSummaryKeys {
  /** `{dishes}` and `{parts}` slots: «Leímos {dishes}: {parts}». */
  sentence: string;
  /** `{dishes}` slot: nothing to change («todos tienen el mismo precio…»). */
  upToDate: string;
  /** Two-form key for the bold `{dishes}` slot (`$tc`). */
  dishes: string;
}

/** «Leímos» / «Copiamos» / «Su lista tiene» by where the dishes came from. */
export function updateSummaryKeys(source: TPublishSource): IUpdateSummaryKeys {
  const dishes = 'menuImport.review.summaryDishes';
  if (source === 'read')
    return {
      sentence: 'menuImport.update.summaryRead',
      upToDate: 'menuImport.update.summaryUpToDate',
      dishes,
    };
  if (source === 'copy')
    return {
      sentence: 'menuImport.update.summaryCopied',
      upToDate: 'menuImport.update.summaryCopiedUpToDate',
      dishes,
    };
  return {
    sentence: 'menuImport.update.summaryOwn',
    upToDate: 'menuImport.update.summaryOwnUpToDate',
    dishes,
  };
}

/** What the summary says after «Encontramos …». */
export type TSummaryStatus = 'toCheck' | 'allSet';

/**
 * Amber «{count} para revisar» while dishes are flagged (`summary.toCheck`:
 * the amber and red dishes, the same count as the «Para revisar» chip; open
 * questions are shown by their own cards); emerald «Todo listo para
 * publicar» only when nothing blocks publishing; nothing while only
 * blocking questions are left (their cards ask for the answer).
 */
export function summaryStatus(
  summary: Pick<IDraftSummary, 'toCheck' | 'openBlockingQuestions'>,
  incomplete = false
): TSummaryStatus | null {
  if (summary.toCheck > 0) return 'toCheck';
  // After an incomplete read (`isReadIncomplete`) the partial line or the
  // page cards explain what is missing: nothing claims «Todo listo para
  // publicar» next to them.
  return summary.openBlockingQuestions > 0 || incomplete ? null : 'allSet';
}

export interface IReadDoneInput {
  /** Flagged dishes (`draftSummary(draft).toCheck`, the «Para revisar» chip's count). */
  toReview: number;
  openBlockingQuestions: number;
  /** The read left something out: it ended `partial`, or a page failed or was too dense. */
  incomplete: boolean;
  /** An update session with nothing to change (`liveHasChanges` false). */
  upToDate: boolean;
}

/**
 * The one-time «Terminamos de leer» card (§4.4 End): «{count} cosas para
 * revisar» while dishes are flagged, «Todo listo para publicar» only when
 * nothing blocks and the read left nothing out. null: no card (a partial
 * read and failed pages have their own lines, blocking questions their
 * cards).
 */
export function readDoneText(input: IReadDoneInput): IBarText | null {
  if (input.toReview > 0)
    return { key: 'menuImport.reading.doneLine', count: input.toReview };
  if (input.openBlockingQuestions > 0 || input.incomplete) return null;
  if (input.upToDate)
    return { key: 'menuImport.update.upToDateCard', count: null };
  return { key: 'menuImport.reading.doneAllGood', count: null };
}

/** «Revisar mi menú»: the first amber or red dish, in menu order. */
export function firstFlaggedItemId(draft: IMenuDraft): string | null {
  return (
    orderedItems(draft).find((item) => item.review.level !== 'ok')?.id ?? null
  );
}

/**
 * «Revisar mi menú» has somewhere to go: a flagged dish, else an open
 * question. Without either the done card shows no button (a tap would do
 * nothing; review round 4, m1).
 */
export function hasSomethingToReview(draft: IMenuDraft): boolean {
  return firstFlaggedItemId(draft) !== null || openQuestions(draft).length > 0;
}

/**
 * The side panel («Pedir cambios» | «Carta original») has something to
 * offer: the chat while the AI is on, or menu photos to look at. With the AI
 * off and no photo page there is no side pane, no floating «Pedir cambios»
 * and no «Carta original» entry (review round 4, m4).
 */
export function sidePanelUseful(
  aiEnabled: boolean,
  pages: IMenuImportPage[]
): boolean {
  return aiEnabled || viewerPages(pages).length > 0;
}

export type TPublishBlocker =
  | { kind: 'item'; id: string }
  | { kind: 'question'; id: string };

/**
 * What a tap on the blocked «Publicar menú» opens: the first dish missing a
 * price (fixable in its row), else the first blocking question, else the
 * first red dish.
 */
export function publishBlockerTarget(
  draft: IMenuDraft
): TPublishBlocker | null {
  const itemId = firstRedItemId(draft);
  const item = itemId !== null ? draft.items[itemId] : undefined;
  if (item && item.review.reason !== 'question')
    return { kind: 'item', id: item.id };
  const question = openQuestions(draft).find((open) => open.blocking);
  if (question) return { kind: 'question', id: question.id };
  return item ? { kind: 'item', id: item.id } : null;
}

/** «Listo. Cambiamos {count} platos.»: the dishes an answer changed or removed. */
export function changedItemCount(delta: IDraftDelta | null): number {
  if (!delta) return 0;
  return (delta.items?.length ?? 0) + (delta.removed?.items?.length ?? 0);
}

/** «Ocultar {count} sin precio»: one `updateItem { hidden: true }` per visible dish without a price. */
export function hideUnpricedOps(draft: IMenuDraft): TDraftOp[] {
  return unpricedVisibleItemIds(draft).map(
    (itemId): TDraftOp => ({ op: 'updateItem', itemId, hidden: true })
  );
}

/** Longest dish or category name the API accepts (§8.11). */
export const DRAFT_NAME_MAX = 128;

/** A typed category or dish name, trimmed; null when empty or too long. */
export function cleanDraftName(text: string): string | null {
  const name = text.trim();
  return name.length > 0 && name.length <= DRAFT_NAME_MAX ? name : null;
}

/** «Seguir después»: the dashboard in the setup flow, the items page on `/menu/import`. */
export function menuLaterTarget(
  entry: 'setup' | 'import',
  stringId: string,
  /** The Menú page is on for the store (plan §5.1). */
  menuV2 = false
): string {
  return entry === 'setup'
    ? `/${stringId}/dashboard`
    : menuPagePath(stringId, menuV2);
}

// ---------------------------------------------------------------------------
// Bottom bar
// ---------------------------------------------------------------------------

/** A bar text: plural when `count` is a number (`$tc`). */
export interface IBarText {
  key: string;
  count: number | null;
}

/**
 * «Cargar otra vez» (bottom bar and the ⋯ menu) leads somewhere new: the AI
 * can read more pages, or the menu is still empty, where the source screen
 * also offers «Copiar el menú de …» (and «Omitir por ahora» in the setup).
 * With the AI off and dishes in the menu it would only offer typing them
 * (review round 4, m4; round 5, m2).
 */
export function readAgainOffered(
  aiEnabled: boolean,
  hasItems: boolean
): boolean {
  return aiEnabled || !hasItems;
}

export interface IWorkspaceBarModel {
  /** «Cargar otra vez» (wide only, `readAgainOffered`). */
  left: IBarText | null;
  helper: IBarText | null;
  /** The helper does what the blocked CTA does (opens what is missing). */
  helperIsLink: boolean;
  /** «Seguir después» (wide only). */
  secondary: IBarText | null;
  primary: IBarText | null;
  primaryDisabled: boolean;
  /** Orange and tappable, but announced as unavailable: a tap opens what blocks publishing. */
  primaryAriaDisabled: boolean;
  /** The primary shows its loading state (an update checks the menu before publishing). */
  primaryBusy: boolean;
}

/** What «Publicar» does once the draft was compared with the menu (§3.6–3.7). */
export type TPublishPath = 'stop' | 'blocked' | 'finish' | 'dialog';

/** The parts of the flow bar `publishPath` reads. */
export interface IFlowBarLike {
  primaryLabel: string | null;
  primaryDisabled: boolean;
  primaryAriaDisabled: boolean;
}

export interface IPublishPathInput {
  /** The bar of the screen on show (null: none). */
  bar: IFlowBarLike | null;
  /** The draft was matched with the store's menu. */
  update: boolean;
  /** Publishing changes something (`liveHasChanges`). */
  hasChanges: boolean;
  /** Amber and red dishes. */
  toCheck: number;
  /** Every change was saved (the dialog shows the save state otherwise). */
  saved: boolean;
}

/**
 * `stop`: nothing to press any more (offline, a read started); `blocked`:
 * something to fix first (the bar opens it); `finish`: an update that changes
 * nothing, with nothing to check, publishes without the dialog; else the
 * dialog.
 */
export function publishPath(input: IPublishPathInput): TPublishPath {
  const { bar } = input;
  if (!bar || bar.primaryLabel === null || bar.primaryDisabled) return 'stop';
  if (bar.primaryAriaDisabled) return 'blocked';
  return input.update && !input.hasChanges && input.toCheck === 0 && input.saved
    ? 'finish'
    : 'dialog';
}

/** An update session (menu update from photos): what the bar needs to know. */
export interface IWorkspaceBarUpdate {
  /** Publishing changes something (`liveHasChanges`). */
  hasChanges: boolean;
  /** Dishes to check (the «Para revisar» count). */
  toCheck: number;
  /** The menu is being checked before publishing. */
  refreshing: boolean;
}

export interface IWorkspaceBarInput {
  phase: TWorkspacePhase;
  wide: boolean;
  online: boolean;
  /** The AI can read pages (`readAgainOffered`). */
  aiEnabled: boolean;
  summary: IDraftSummary;
  /** null outside update sessions. */
  update: IWorkspaceBarUpdate | null;
}

function text(key: string, count: number | null = null): IBarText {
  return { key, count };
}

const EMPTY_BAR: IWorkspaceBarModel = {
  left: null,
  helper: null,
  helperIsLink: false,
  secondary: null,
  primary: null,
  primaryDisabled: false,
  primaryAriaDisabled: false,
  primaryBusy: false,
};

/** The bottom bar of the workspace (§4.4 «Bars», §4.5 «Bottom bar»). */
export function workspaceBarModel(
  input: IWorkspaceBarInput
): IWorkspaceBarModel {
  const { phase, wide, online, aiEnabled, summary, update } = input;
  const publish = text(
    update ? 'menuImport.update.publish' : 'menuImport.bar.publish'
  );
  // The failed state carries its own actions.
  if (phase === 'failed') return EMPTY_BAR;
  if (phase === 'reading')
    return {
      ...EMPTY_BAR,
      helper: text('menuImport.reading.nothingPublishedYet'),
      primary: publish,
      primaryDisabled: true,
    };

  const base: IWorkspaceBarModel = {
    ...EMPTY_BAR,
    left:
      wide && readAgainOffered(aiEnabled, summary.items > 0)
        ? text('menuImport.bar.readAgain')
        : null,
    secondary: wide ? text('menuImport.bar.later') : null,
    primary: publish,
    primaryBusy: update !== null && update.refreshing,
  };
  if (summary.items === 0)
    return {
      ...base,
      helper: text('menuImport.bar.emptyDraft'),
      primaryDisabled: true,
    };

  const prices = summary.missingPrices;
  const questions = summary.openBlockingQuestions;
  if (summary.fix === 0 && questions === 0) {
    // An update that changes nothing: «Su menú ya está al día» · «Terminar».
    if (update && !update.hasChanges && update.toCheck === 0)
      return {
        ...base,
        helper: text('menuImport.update.upToDate'),
        primary: text('menuImport.update.finish'),
        primaryDisabled: !online,
      };
    return {
      ...base,
      helper: text('menuImport.reading.nothingPublishedYet'),
      primaryDisabled: !online,
    };
  }

  if (prices > 0)
    return {
      ...base,
      helper: text('menuImport.bar.completePrices', prices),
      helperIsLink: true,
      primary: text('menuImport.bar.completePricesShort', prices),
      primaryDisabled: !online,
      primaryAriaDisabled: true,
    };
  if (questions > 0)
    return {
      ...base,
      helper: text('menuImport.bar.answerQuestions', questions),
      helperIsLink: true,
      primary: text('menuImport.bar.answerQuestionsShort', questions),
      primaryDisabled: !online,
      primaryAriaDisabled: true,
    };
  // Red for another reason (no price among its sizes, a stale question): opening it tells the owner.
  return {
    ...base,
    helper: text('menuImport.reading.nothingPublishedYet'),
    primaryDisabled: !online,
    primaryAriaDisabled: true,
  };
}
